export type OrderAddress = {
  address_line_1?: string | null;
  address_line_2?: string | null;
  address_line_3?: string | null;
  company?: string | null;
  locality?: string | null;
  postal_code?: string | null;
  administrative_district_level_1?: string | null;
  country_code?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  editable?: boolean | null;
};

export type OrderPickupPerson = {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
};

export type OrderPickupLocation = {
  partner_store?: string;
  location_id?: string;
  location_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
};

export type CardIntent = {
  intent: 'CHARGE' | 'CHARGE_AND_STORE' | 'STORE';
};

export type OrderSummaryChecking = {
  idv_required: boolean;
  idv_verified: boolean;
  ineligible_reason: string | null;
};

export type OrderSummaryContactInformation = {
  email?: string;
  phone?: string;
};

export type OrderSummaryCurrentUser = {
  is_logged_in: boolean;
  email: string | null;
  merchant_token: string;
  is_pnp_subscribed: boolean;
  active_location_count: number | null;
};

export type OrderSummaryIdv = {
  block_suspended: boolean;
  required: boolean;
  status: string | null;
};

export type OrderSummaryPaymentDue = {
  payment_schedule: string;
  amount: string;
  currency: string;
  installment_first_payment_amount: string;
};

export type OrderSummaryPaymentInformation = {
  expiration_month?: string;
  expiration_year?: string;
  brand?: string;
  last4?: string;
  cashtag?: string;
  payment_covers_order_total?: boolean | null;
};

export type OrderSummaryShipping = {
  label: string;
  amount: string;
  currency: string;
};

export type OrderSummaryShippingEstimate = {
  code?: string;
  verb?: string | null;
  unit?: string | null;
  min?: number | null;
  max?: number | null;
};

export type OrderSummarySubtotal = {
  label: string;
  amount: string;
  currency: string;
};

export type OrderSummaryTaxBreakdown = {
  label: string;
  amount: string;
  included: boolean;
  currency: string;
  type: string;
  tax_rate?: string;
};

export type OrderSummaryTaxNumberInformation = {
  country_code: string | null;
  number: string | null;
};

export type OrderSummaryTotal = {
  label: string;
  amount: string;
  currency: string;
};

export type CartSummaryPart = {
  base_product_key: string;
  product_key: string;
  name: string;
  category: string | null;
  slug: string;
};

export type ContentfulContent = {
  data: Record<string, unknown>;
  content: ContentfulContent[];
  nodeType: string;
  value?: string;
  marks?: unknown[];
  uri?: string;
};

export type ProductVariantDescription = {
  data: Record<string, unknown>;
  content: ContentfulContent[];
  nodeType: string;
};

export type CartSummaryProductVariant = {
  token: string;
  title: string;
  description: ProductVariantDescription | null;
  slug: string;
};

export type Properties = {
  featured: boolean;
  financing_ineligible: boolean;
  warranty_period: number;
  self_serve: boolean;
  effa: boolean;
  warranty_disabled: boolean;
  no_ship: boolean;
  check_software_eligibility: boolean;
};

export enum PlanRenewalCycleUnit {
  DAY = 'DAY',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export type CartSummaryPlan = {
  token: string;
  name: string;
  free_trial_duration: {
    count: number;
    unit: string;
  };
  renewal_cycle: {
    count: number;
    unit: PlanRenewalCycleUnit;
  };
  pricing_model: {
    fixed_pricing: {
      cost: {
        currency_code: string;
        amount: number;
      };
    };
    bucket_pricing: {
      cost: {
        currency_code: string;
        amount: number;
      };
    };
  };
};

export type OrderSummaryCartSummary = {
  amount: string;
  base_product_key: string;
  category: string;
  currency: string;
  exchange_serial_number: string | null;
  has_pdp: boolean;
  id: number;
  img: string | null;
  is_checking_product: boolean;
  is_software: boolean;
  individual_sale: boolean;
  max_quantity: number;
  notifications: string | null;
  out_of_stock: boolean;
  parts: CartSummaryPart[];
  plan: CartSummaryPlan | null;
  pobox_eligible: boolean;
  price: string;
  product_key: string;
  product_variant: CartSummaryProductVariant;
  properties: Properties;
  quantity: number;
  software_group: string | null;
  user_eligible: boolean;
};

export type OrderSummaryInstallmentSummary = {
  application_flow_token?: string;
  token?: string;
  number_payment_periods?: number;
  financed_amount?: {
    amount: string;
    currency_code: string;
  };
  payment_amount?: {
    amount: string;
    currency_code: string;
  };
  final_payment_amount?: {
    amount: string;
    currency_code: string;
  };
  total_owed_amount?: {
    amount: string;
    currency_code: string;
  };
  fee_amount?: {
    amount: string;
    currency_code: string;
  };
};

export type OrderSummaryDiscountAdjustment = {
  amount: string;
  type: 'line_item' | 'order' | 'shipment';
};

export type OrderSummaryDiscount = {
  code: string;
  name: string;
  currency: string;
  auto_applied: boolean;
  limit_reached: boolean;
  adjustments: OrderSummaryDiscountAdjustment[];
};

type OrderSummaryIssuerInstallmentsSummary = {
  number_of_payments_selected?: number;
  quote_payments_amount_unformatted?: string;
  quote_payments_amount?: string;
  quote_number_of_payments?: number;
};

type OrderSummarySoftwareSummary = {
  // keys are `product.sq_product_key` values
  [key: string]: {
    subscription_state?: string;
    free_trial_end_date?: string;
  };
};

type OrderSummaryAdditionalTax = {
  label: string;
  amount: string;
  currency: string;
  included: boolean;
};

type OrderSummaryIncludedTax = {
  label: string;
  amount: string;
  currency: string;
  included: boolean;
};

export type BundleOfferTierKey = 'free' | 'plus' | 'premium';

export type BundleOfferProcessingRate = {
  rate_type: 'card_present' | 'online';
  variable_basis_points: number;
  fixed_cents: number;
};

export type BundleOfferTier = {
  tier: BundleOfferTierKey;
  label: string | null;
  monthly_cents: number | null;
  currency: string | null;
  trial_days: number;
  variant_token: string | null;
  processing_rates: BundleOfferProcessingRate[];
  feature_bullets: string[];
};

// `hardware_bundle_offer` carries the Square One bundle resolver output and LD
// bucket metadata for the cart and payment surfaces. The serializer returns null
// when the experiment is off or the cart doesn't qualify. See
// Solidshop::Software::HardwareBundleOfferViewModel.
export type HardwareBundleOffer = {
  resolution:
    | 'plus'
    | 'premium'
    | 'upgrade_to_premium'
    | 'upgrade_in_cart'
    | 'already_subscribed'
    | 'ineligible_to_migrate'
    | 'suppress';
  recommended_tier: BundleOfferTierKey | null;
  active_tier: BundleOfferTierKey | null;
  experiment_name: string;
  experiment_variation: string;
  tiers: Record<BundleOfferTierKey, BundleOfferTier>;
};

export type TOrderSummary = {
  additional_tax: OrderSummaryAdditionalTax | null;
  billing_address: OrderAddress;
  bopis_eligible: boolean;
  bopis_fulfillment_selected: boolean;
  card_intent: CardIntent;
  checking: OrderSummaryChecking;
  completed: boolean;
  contact_information: OrderSummaryContactInformation;
  current_user: OrderSummaryCurrentUser;
  discounts: OrderSummaryDiscount[];
  first_party_payment_only: boolean;
  idv: OrderSummaryIdv;
  included_tax: OrderSummaryIncludedTax | null;
  installment_summary: OrderSummaryInstallmentSummary;
  issuer_installments_summary: OrderSummaryIssuerInstallmentsSummary;
  is_hardware_rental_order: boolean;
  is_northern_ireland_order: boolean;
  is_warranty: boolean;
  items_count: number;
  no_ship: boolean;
  payment_due: OrderSummaryPaymentDue;
  payment_information: OrderSummaryPaymentInformation;
  pickup_location_info: OrderPickupLocation;
  pickup_person_info: OrderPickupPerson;
  rental_idv_submission_blocked: boolean;
  search_postal_code: string | null;
  shipping: OrderSummaryShipping;
  shipping_address: OrderAddress;
  shipping_estimate: OrderSummaryShippingEstimate;
  subtotal: OrderSummarySubtotal;
  tax_breakdown: OrderSummaryTaxBreakdown[];
  tax_number_information: OrderSummaryTaxNumberInformation;
  total: OrderSummaryTotal;
  upfront_card_payment_only: boolean;
  zero_tax_rate: boolean | null;
  software_summary: OrderSummarySoftwareSummary;
  cart_summary: OrderSummaryCartSummary[];
  hardware_bundle_offer: HardwareBundleOffer | null;
};
